import React, { useState, useEffect, useCallback } from "react";
import ActionDropdown from "../common/ui/actiondropdown";
import AddMaterial from "./addmaterial";
import { refreshAccessToken, API_CONFIG, getHeaders } from "../../config/api";
import MaterialHeading from "./materialheading";
import { useLoader } from "../../context/loadercontext"; // 👈 global loader context
import toast from "react-hot-toast";
import { DeletePreviewModal, FinalDeleteConfirmModal } from "../common/ui/deletemodal";
import { useAuth } from "../../context/AuthContext"; // Auth context
import NoFound from "../common/ui/NoFound";
import WarningModal from "../common/ui/warningmodel";

export default function ViewMaterial() {
  const [materials, setMaterials] = useState([]);
  const { setLoading } = useLoader();
  const [openRowIndex, setOpenRowIndex] = useState(null);
  const [showDeletePreview, setShowDeletePreview] = useState(false); // 🆕 first modal
  const [showFinalDeleteConfirm, setShowFinalDeleteConfirm] = useState(false); // 🆕 second modal
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  // 🔹 Add/Edit Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const { logout } = useAuth();

  // 🔹 Fetch all materials
  const fetchMaterials = useCallback(async () => {
    setLoading(true); // 👈 show loader immediately
    const startTime = Date.now();
    try {
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.GET_ALL}`,
        { method: "GET", headers: getHeaders(true) }
      );

      // -------------------------
      // 🔥 If ACCESS TOKEN expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.GET_ALL}`,
            { method: "GET", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }
      const result = await response.json();
      if (result.status === 200) {
        const mappedData = result.data.map((m) => ({
          id: m.id,
          materialName: m.name,
          shortCode: m.short_code,
          hsnCode: m.hsn_code,
          basicDuty: m.basic_duty_pr,
          socialDuty: m.social_duty_pr,
          Igst: m.igst_pr,
          createdAt: m.created_at
        }));
        setMaterials(mappedData);
      }
    } catch (error) {
      console.error("Error fetching materials:", error);
    } finally {
      const elapsed = Date.now() - startTime;
      const remainingDelay = Math.max(0, 1000 - elapsed);

      setTimeout(() => {
        setLoading(false); // 👈 hide loader after API time + 1s total
      }, remainingDelay);
    }
  }, [setLoading, logout]);

  // Fetch on first load
  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  const handleRowToggle = (index) => {
    setOpenRowIndex(openRowIndex === index ? null : index);
  };

  const handleEdit = (index) => {
    setEditData({ ...materials[index], index });
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditData(null);
  };

  // 🆕 Step 1: Open preview modal before delete confirmation
  const handleDeleteClick = (index) => {
    setDeleteIndex(index);
    setShowDeletePreview(true);
  };

  // 🆕 Step 2: After user confirms preview, show final confirmation modal
  const handleProceedToFinalConfirm = () => {
    setShowDeletePreview(false);
    setShowFinalDeleteConfirm(true);
  };

  const handleCancelPreview = () => {
    setShowDeletePreview(false);
    setDeleteIndex(null);
  };

  // 🧾 Final confirmation — perform delete API
  const handleConfirmDelete = async () => {
    try {
      const materialId = materials[deleteIndex]?.id;
      if (!materialId) return;
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.SOFT_DELETE(materialId)}`,
        { method: "PUT", headers: getHeaders(true) }
      );

      // -------------------------
      // 🔥 If ACCESS TOKEN expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.SOFT_DELETE(materialId)}`,
            { method: "PUT", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }

      const result = await response.json();

      if (response.ok && (result.status === 200 || result.message?.includes("deleted"))) {
        toast.success("Material deleted successfully");
        await fetchMaterials();
      } else {
        setWarningMessage(result.message || "Failed to delete material");
        setWarningVisible(true);

        console.error("Failed to delete material:", result);
      }
    } catch (error) {
      console.error("Error deleting material:", error);
      setWarningMessage("Error deleting material");
      setWarningVisible(true);
    } finally {
      setShowFinalDeleteConfirm(false);
      setDeleteIndex(null);
    }
  };

  //Format Date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <>
      {/* Header with Add Button */}
      <MaterialHeading onAddClick={() => setShowAddModal(true)} />

      {/* Table */}
      <div className="mt-6 overflow-x-auto">
        <table className="min-w-[950px] w-full text-table text-center">
          <thead className="border-b border-gray-200 text-gray-600 font-medium">
            <tr>
              <th className="px-4 py-3">Short Code</th>
              <th className="px-4 py-3">Material Name</th>
              <th className="px-4 py-3">HSN Code</th>
              <th className="px-4 py-3">Basic Duty</th>
              <th className="px-4 py-3">Social Duty</th>
              <th className="px-4 py-3">IGST</th>
              <th className="px-4 py-3">Created At</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {materials.length > 0 ? (
              materials.map((row, idx) => (
                <tr
                  key={row.id || idx}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowToggle(idx)}
                >
                  <td className="px-3 py-1">
                    <div className="flex justify-center mt-1">
                      {(() => {
                        const colorPairs = [
                          { bg: "#edeff1", text: "#1c2a3d" },
                          { bg: "#dbf6e5", text: "#25915a" },
                          { bg: "#fff1d6", text: "#ce880d" },
                          { bg: "#ffe4de", text: "#b71d18" },
                          { bg: "#d6f4f9", text: "#1a7da7" },
                        ];

                        const randomColor = colorPairs[Math.floor(Math.random() * colorPairs.length)];

                        return (
                          <div
                            className="w-14 h-6 flex items-center justify-center font-semibold text-xs text-center rounded uppercase shadow-sm"
                            style={{
                              backgroundColor: randomColor.bg,
                              color: randomColor.text,
                            }}
                          >
                            {row.shortCode || "-"}
                          </div>

                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-3 py-1">
                    {row.materialName
                      ? row.materialName
                        .toLowerCase()
                        .split(" ")
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(" ")
                      : ""}
                  </td>
                  <td className="px-3 py-1">{row.hsnCode}</td>
                  <td className="px-3 py-1">{row.basicDuty} %</td>
                  <td className="px-3 py-1">{row.socialDuty} %</td>
                  <td className="px-3 py-1">{row.Igst} %</td>
                  <td className="px-3 py-1">{formatDate(row.createdAt)}</td>
                  <td
                    className="px-3 py-1 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionDropdown
                      actions={{ edit: true, delete: true }}
                      onEdit={() => handleEdit(idx)}
                      onDelete={() => handleDeleteClick(idx)}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="py-4 text-gray-500">
                  <NoFound />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      <AddMaterial
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onMaterialAdded={fetchMaterials}
      />

      {/* Edit Modal */}
      <AddMaterial
        isOpen={showEditModal}
        onClose={handleCloseEdit}
        editData={editData}
        onMaterialAdded={fetchMaterials}
      />

      {/* 🧩 Reusable Modals */}
      {showDeletePreview && deleteIndex !== null && (
        <DeletePreviewModal
          title="Material Details"
          data={{
            Name: materials[deleteIndex]?.materialName,
            ShortCode: materials[deleteIndex]?.shortCode,
            HSNCode: materials[deleteIndex]?.hsnCode,
            BasicDuty: `${materials[deleteIndex]?.basicDuty} %`,
            SocialDuty: `${materials[deleteIndex]?.socialDuty} %`,
            IGST: `${materials[deleteIndex]?.Igst} %`,
            "Created At": materials[deleteIndex]?.createdAt?.split("T")[0],
          }}
          onCancel={handleCancelPreview}
          onProceed={handleProceedToFinalConfirm}
        />
      )}

      {showFinalDeleteConfirm && (
        <FinalDeleteConfirmModal
          title="Confirm Delete"
          message="Are you sure you want to permanently delete this section?"
          onCancel={() => setShowFinalDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}

    </>
  );
}
